import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

const LeaveTypeContext = createContext();
export const useLeaveTypes = () => useContext(LeaveTypeContext);

export const LeaveTypeProvider = ({ children }) => {
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userId = user?.id || null;

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Leave Types
  const fetchLeaveTypes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/leave-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.status === 401) {
        setError("Session expired. Please log in again.");
        return;
      }

      if (!res.ok) {
        setError("Failed to fetch leave types");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setLeaveTypes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch leave types");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching leave types");
    } finally {
      setLoading(false);
    }
  };

  // Create Leave Type
  const addLeaveType = async (newData) => {
    try {
      const token = getToken();

      const payload = {
        ...newData,
        create_audit_id: userId,
      };

      const res = await fetch(`${BASE_URL}/leave-types`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to add leave type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchLeaveTypes();

        toast.success(data.message || "Leave type added successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to add leave type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error adding leave type",
      };
    }
  };

  // Update Leave Type
  const updateLeaveType = async (id, updatedData) => {
    try {
      const token = getToken();

      const payload = {
        ...updatedData,
        update_audit_id: userId,
      };

      const res = await fetch(`${BASE_URL}/leave-types/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update leave type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaveTypes();

        toast.success(data.message || "Leave type updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update leave type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating leave type",
      };
    }
  };

  // Update Status
  const updateLeaveTypeStatus = async (id, status) => {
    try {
      const token = getToken();

      const payload = {
        status,
        update_audit_id: userId,
      };

      const res = await fetch(`${BASE_URL}/leave-types/updateStatus/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaveTypes();

        toast.success(data.message || "Leave type status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating status",
      };
    }
  };

  // Delete Leave Type
  const deleteLeaveType = async (id) => {
    try {
      const token = getToken();

      const payload = {
        delete_audit_id: userId,
      };

      const res = await fetch(`${BASE_URL}/leave-types/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete leave type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchLeaveTypes();

        toast.success(data.message || "Leave type deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete leave type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error deleting leave type",
      };
    }
  };

  useEffect(() => {
    const token = getToken();
    if (token) fetchLeaveTypes();
  }, []);

  return (
    <LeaveTypeContext.Provider
      value={{
        leaveTypes,
        loading,
        error,
        fetchLeaveTypes,
        addLeaveType,
        updateLeaveType,
        updateLeaveTypeStatus,
        deleteLeaveType,
      }}
    >
      {children}
    </LeaveTypeContext.Provider>
  );
};